import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/components/common";
import { EmptyState } from "@/components/telecaller/LeadCard";
import { CalendarClock, Phone, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function bucketOf(f) {
  const now = new Date();
  const d = new Date(f.scheduled_at);
  if (f.status !== "PENDING") return f.status === "COMPLETED" ? "done" : "other";
  const sameDay = d.toDateString() === now.toDateString();
  if (d < now && !sameDay) return "missed";
  if (sameDay) return "today";
  if (d < now) return "missed";
  return "upcoming";
}

export default function Followups() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = () => api.get("/followups").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    try { await api.patch(`/followups/${id}`, { status: "COMPLETED" }); toast.success("Follow-up completed"); load(); }
    catch { toast.error("Couldn't update. Please try again."); }
  };
  const call = async (e, f) => { e.stopPropagation(); try { await api.post(`/leads/${f.lead_id}/call`); } catch {} window.location.href = `tel:${f.lead?.mobile}`; };
  const wa = async (e, f) => { e.stopPropagation(); try { const { data } = await api.post(`/leads/${f.lead_id}/whatsapp`, {}); if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank"); } catch {} };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const groups = { today: [], upcoming: [], missed: [] };
  items.forEach((f) => { const b = bucketOf(f); if (groups[b]) groups[b].push(f); });

  const Row = (f) => (
    <div key={f.id} data-testid={`followup-card-${f.id}`} onClick={() => navigate(`/app/leads/${f.lead_id}`)}
      className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 cursor-pointer">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold text-[15px]">{f.lead?.name || "Lead"} <span className="font-mono text-[11px] text-slate-500">#{f.lead?.lead_number}</span></div>
          <div className="text-xs text-slate-400 mt-0.5">{fmtDate(f.scheduled_at)}</div>
          {f.note && <div className="text-xs text-slate-500 mt-1">{f.note}</div>}
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${f.status === "COMPLETED" ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`}>{f.status}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <button onClick={(e) => call(e, f)} className="flex items-center justify-center gap-1.5 rounded-xl bg-primary text-primary-foreground py-2 text-xs font-bold"><Phone className="h-3.5 w-3.5" />Call</button>
        <button onClick={(e) => wa(e, f)} className="flex items-center justify-center gap-1.5 rounded-xl border border-emerald-500/40 text-emerald-400 py-2 text-xs font-bold"><MessageCircle className="h-3.5 w-3.5" />WA</button>
        {f.status === "PENDING"
          ? <button onClick={(e) => { e.stopPropagation(); complete(f.id); }} data-testid={`complete-followup-${f.id}`} className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-700 text-slate-200 py-2 text-xs font-bold"><CheckCircle2 className="h-3.5 w-3.5" />Done</button>
          : <div className="flex items-center justify-center text-xs text-slate-500">✓</div>}
      </div>
    </div>
  );

  const anyPending = groups.today.length + groups.upcoming.length + groups.missed.length;

  return (
    <div className="space-y-5">
      <header><h1 className="font-heading text-xl font-extrabold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" />Follow-ups</h1></header>
      {anyPending === 0 && <EmptyState icon={CalendarClock} title="No follow-ups due today." subtitle="You're all set." />}
      {[["today", "Today"], ["missed", "Missed"], ["upcoming", "Upcoming"]].map(([k, label]) => (
        groups[k].length > 0 && (
          <section key={k}>
            <h2 className={`text-xs uppercase tracking-wide mb-2 ${k === "missed" ? "text-red-400" : "text-slate-400"}`}>{label} · {groups[k].length}</h2>
            <div className="space-y-3">{groups[k].map(Row)}</div>
          </section>
        )
      ))}
    </div>
  );
}
